import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  UserIcon,
  XMarkIcon,
  PencilIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

interface Member {
  id: string;
  username: string;
  name: string;
  email: string;
  user_type: 'adult' | 'minor' | 'institution' | 'admin';
  phone: string | null;
  birthdate: string | null;
  gender: 'male' | 'female' | null;
  address: string | null;
  address_detail: string | null;
  created_at: string;
  is_verified: boolean;
  // 보호자 정보 (14세 미만)
  guardian_name: string | null;
  guardian_phone: string | null;
  // 유입경로
  referral_source: string | null;
}

interface MemberStats {
  application_count: number;
  // children_count 제거 - 본인 신청 구조로 변경됨
}

const MemberManagement: React.FC = () => {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState<string>('all');

  // 상세 모달
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [memberStats, setMemberStats] = useState<MemberStats | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);

  // 전화번호 수정
  const [editingField, setEditingField] = useState<'phone' | 'guardian_phone' | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('profiles')
        .select('id, username, name, email, user_type, phone, birthdate, gender, address, address_detail, created_at, is_verified, guardian_name, guardian_phone, referral_source')
        .order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) {
        console.error('Error loading members:', error);
        alert('회원 목록을 불러오는데 실패했습니다.');
        return;
      }

      console.log('=== 로드된 회원 목록 ===');
      console.log('전체 회원 수:', data?.length);
      console.log('회원 데이터:', data);
      console.log('회원 유형별 분포:', data?.reduce((acc: any, m: any) => {
        acc[m.user_type] = (acc[m.user_type] || 0) + 1;
        return acc;
      }, {}));

      setMembers(data || []);
    } catch (error) {
      console.error('Error:', error);
      alert('오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadMemberStats = async (memberId: string) => {
    setLoadingStats(true);
    try {
      // 신청 건수 조회 (본인 신청만 - registrant_id 기준)
      // guardian_id 조건 제거: 본인 신청 구조로 변경됨
      const { count: applicationCount } = await supabase
        .from('applications')
        .select('*', { count: 'exact', head: true })
        .eq('registrant_id', memberId);

      // children 테이블 조회 제거 - 레거시 구조
      setMemberStats({
        application_count: applicationCount || 0,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleMemberClick = (member: Member) => {
    setSelectedMember(member);
    setShowDetailModal(true);
    loadMemberStats(member.id);
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedMember(null);
    setMemberStats(null);
    setEditingField(null);
    setEditValue('');
  };

  const startEditPhone = (field: 'phone' | 'guardian_phone') => {
    if (!selectedMember) return;
    setEditingField(field);
    setEditValue(selectedMember[field] || '');
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  const savePhone = async () => {
    if (!selectedMember || !editingField) return;

    const normalized = editValue.replace(/[^0-9]/g, '');
    if (normalized && (normalized.length < 10 || normalized.length > 11)) {
      alert('올바른 전화번호 형식을 입력해주세요.');
      return;
    }

    setSaving(true);
    try {
      // 중복 체크
      if (normalized) {
        const { data: existing } = await supabase
          .from('profiles')
          .select('id, name, username')
          .or(`phone.eq.${normalized},guardian_phone.eq.${normalized}`)
          .neq('id', selectedMember.id)
          .limit(1);

        if (existing && existing.length > 0) {
          const msg = `이 번호(${normalized})는 이미 "${existing[0].name}" (${existing[0].username}) 회원에게 등록되어 있습니다.\n\n그래도 변경하시겠습니까?`;
          if (!window.confirm(msg)) {
            setSaving(false);
            return;
          }
        }
      }

      const { error } = await supabase
        .from('profiles')
        .update({ [editingField]: normalized || null, updated_at: new Date().toISOString() })
        .eq('id', selectedMember.id);

      if (error) {
        console.error('Error updating phone:', error);
        alert('전화번호 변경에 실패했습니다.');
        return;
      }

      // 로컬 상태 업데이트
      const updated = { ...selectedMember, [editingField]: normalized || null };
      setSelectedMember(updated);
      setMembers(prev => prev.map(m => m.id === updated.id ? updated : m));
      setEditingField(null);
      setEditValue('');
      alert('전화번호가 변경되었습니다.');
    } catch (error) {
      console.error('Error:', error);
      alert('오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const getUserTypeLabel = (userType: string) => {
    switch (userType) {
      case 'adult': return '14세 이상';
      case 'minor': return '14세 미만';
      case 'institution': return '기관';
      case 'admin': return '관리자';
      default: return userType;
    }
  };

  const getUserTypeBadgeColor = (userType: string) => {
    switch (userType) {
      case 'adult': return 'bg-blue-100 text-blue-800';
      case 'minor': return 'bg-green-100 text-green-800';
      case 'institution': return 'bg-purple-100 text-purple-800';
      case 'admin': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // 필터링된 회원 목록
  const filteredMembers = members.filter(member => {
    const matchesSearch =
      member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.username?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesUserType = userTypeFilter === 'all' || member.user_type === userTypeFilter;

    return matchesSearch && matchesUserType;
  });

  return (
    <div>
      {/* 페이지 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">회원 관리</h1>
        <p className="text-sm text-gray-600 mt-1">전체 회원 정보를 조회하고 연락처를 수정할 수 있습니다</p>
      </div>

      {/* 검색 및 필터 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* 검색 */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="이름, 이메일, 아이디 검색"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* 회원 유형 필터 */}
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <select
              value={userTypeFilter}
              onChange={(e) => setUserTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">전체 유형</option>
              <option value="adult">14세 이상</option>
              <option value="minor">14세 미만</option>
              <option value="institution">기관</option>
              <option value="admin">관리자</option>
            </select>
          </div>
        </div>

        {/* 결과 카운트 */}
        <div className="mt-3 text-sm text-gray-600">
          총 <span className="font-bold text-indigo-600">{filteredMembers.length}</span>명
        </div>
      </div>

      {/* 회원 목록 테이블 */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
            <p className="mt-4 text-sm text-gray-500">로딩 중...</p>
          </div>
        ) : filteredMembers.length === 0 ? (
          <div className="text-center py-12">
            <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-4 text-sm text-gray-500">조건에 맞는 회원이 없습니다.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">이름</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">아이디</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">연락처</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">회원 유형</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">가입일</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredMembers.map((member) => (
                  <tr
                    key={member.id}
                    onClick={() => handleMemberClick(member)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{member.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{member.username}</td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-600">{member.phone || '-'}</div>
                      {member.guardian_phone && (
                        <div className="text-xs text-blue-600 mt-0.5">
                          <span className="text-gray-400">└</span> 보호자: {member.guardian_name || ''} {member.guardian_phone}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getUserTypeBadgeColor(member.user_type)}`}>
                        {getUserTypeLabel(member.user_type)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(member.created_at).toLocaleDateString('ko-KR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 상세 정보 모달 */}
      {showDetailModal && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* 모달 헤더 */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">회원 상세 정보</h2>
              <button
                onClick={handleCloseModal}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-6 w-6 text-gray-500" />
              </button>
            </div>

            {/* 모달 본문 */}
            <div className="px-6 py-4 space-y-6">
              {/* 기본 정보 */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                  <UserIcon className="h-5 w-5 mr-2 text-indigo-600" />
                  기본 정보
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex">
                    <span className="w-24 text-gray-600">이름:</span>
                    <span className="font-medium">{selectedMember.name}</span>
                  </div>
                  <div className="flex">
                    <span className="w-24 text-gray-600">아이디:</span>
                    <span className="font-medium">{selectedMember.username}</span>
                  </div>
                  <div className="flex">
                    <span className="w-24 text-gray-600">이메일:</span>
                    <span className="font-medium">{selectedMember.email}</span>
                  </div>
                  <div className="flex">
                    <span className="w-24 text-gray-600">회원 유형:</span>
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getUserTypeBadgeColor(selectedMember.user_type)}`}>
                      {getUserTypeLabel(selectedMember.user_type)}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-24 text-gray-600 flex-shrink-0">전화번호:</span>
                    {editingField === 'phone' ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          placeholder="전화번호 입력 (비우면 삭제)"
                          className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 w-40"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') savePhone();
                            if (e.key === 'Escape') cancelEdit();
                          }}
                        />
                        <button onClick={savePhone} disabled={saving} className="p-1 text-green-600 hover:bg-green-50 rounded">
                          <CheckIcon className="h-4 w-4" />
                        </button>
                        <button onClick={cancelEdit} className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{selectedMember.phone || '-'}</span>
                        <button onClick={() => startEditPhone('phone')} className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded" title="전화번호 수정">
                          <PencilIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex">
                    <span className="w-24 text-gray-600">생년월일:</span>
                    <span className="font-medium">{selectedMember.birthdate || '-'}</span>
                  </div>
                  <div className="flex">
                    <span className="w-24 text-gray-600">성별:</span>
                    <span className="font-medium">
                      {selectedMember.gender === 'male' ? '남성' : selectedMember.gender === 'female' ? '여성' : '-'}
                    </span>
                  </div>
                  <div className="flex">
                    <span className="w-24 text-gray-600">주소:</span>
                    <span className="font-medium">{selectedMember.address || '-'}</span>
                  </div>
                  {selectedMember.address_detail && (
                    <div className="flex">
                      <span className="w-24 text-gray-600">상세주소:</span>
                      <span className="font-medium">{selectedMember.address_detail}</span>
                    </div>
                  )}
                  <div className="flex">
                    <span className="w-24 text-gray-600">가입일:</span>
                    <span className="font-medium">
                      {new Date(selectedMember.created_at).toLocaleString('ko-KR')}
                    </span>
                  </div>
                  <div className="flex">
                    <span className="w-24 text-gray-600">유입경로:</span>
                    <span className="font-medium">{selectedMember.referral_source || '-'}</span>
                  </div>
                </div>
              </div>

              {/* 보호자 정보 (미성년자인 경우) */}
              {selectedMember.guardian_phone && (
                <div>
                  <h3 className="text-sm font-semibold text-blue-600 mb-3">보호자 정보</h3>
                  <div className="bg-blue-50 rounded-lg p-4 space-y-2 text-sm border border-blue-100">
                    <div className="flex">
                      <span className="w-24 text-gray-600">보호자명:</span>
                      <span className="font-medium">{selectedMember.guardian_name || '-'}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="w-24 text-gray-600 flex-shrink-0">보호자 연락처:</span>
                      {editingField === 'guardian_phone' ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            placeholder="보호자 전화번호 입력"
                            className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 w-40"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') savePhone();
                              if (e.key === 'Escape') cancelEdit();
                            }}
                          />
                          <button onClick={savePhone} disabled={saving} className="p-1 text-green-600 hover:bg-green-50 rounded">
                            <CheckIcon className="h-4 w-4" />
                          </button>
                          <button onClick={cancelEdit} className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{selectedMember.guardian_phone}</span>
                          <button onClick={() => startEditPhone('guardian_phone')} className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded" title="보호자 연락처 수정">
                            <PencilIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* 통계 정보 */}
              {loadingStats ? (
                <div className="text-center py-4">
                  <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-indigo-600 border-r-transparent"></div>
                  <p className="mt-2 text-xs text-gray-500">통계 로딩 중...</p>
                </div>
              ) : memberStats && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">활동 통계</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-blue-600">{memberStats.application_count}</div>
                      <div className="text-xs text-gray-600 mt-1">총 신청 건수</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 모달 푸터 */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberManagement;
